import { Add, Delete } from '@tunasong/icons'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Note, NoteType } from '@tunasong/models'
import { matchAny, noteTemplate, shortUuid } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import {
  features,
  skipToken,
  storageApi,
  useCurrentUser,
  useMimeTypes,
  type RootState,
} from '@tunasong/redux'
import type { CoreElement, Persisted, SlateText } from '@tunasong/schemas'
import { isMusicXML, isPdf } from '@tunasong/schemas'
import { createCollaborativeDoc, textFromYDoc } from '@tunasong/sync-lib'
import type { SidebarComponent } from '@tunasong/ui-lib'
import {
  Button,
  ConfirmDialog,
  EntityContent,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  UploadStoredEntity,
  VBox,
} from '@tunasong/ui-lib'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEditorClass } from '../hooks/index.js'
import styles from './entity-notes.module.css'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

/**
 * Walk the entity's `children` snapshot, returning `id → text` for each element
 * that has both. Used by the panel to render the "Anchored to '...'" subtitle.
 * Stale relative to the live YDoc — same trade-off as the children-snapshot
 * preview path: acceptable for hint-style UI.
 */
const buildElementTextById = (root: unknown): Map<string, string> => {
  const map = new Map<string, string>()
  const walk = (nodes: unknown[]): void => {
    for (const node of nodes) {
      if (!isRecord(node)) {
        continue
      }
      const id = typeof node.id === 'string' ? node.id : null
      const children = Array.isArray(node.children) ? node.children : null
      if (id && children) {
        const text = children
          .map(leaf => (isRecord(leaf) && typeof leaf.text === 'string' ? leaf.text : ''))
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim()
        if (text) {
          map.set(id, text)
        }
      }
      if (children) {
        walk(children)
      }
    }
  }
  if (Array.isArray(root)) {
    walk(root)
  }
  return map
}

/** Truncate the anchor's source-line text to a panel-friendly subtitle. */
const ANCHOR_SUBTITLE_MAX_CHARS = 30
const truncateAnchorPreview = (text: string): string =>
  text.length > ANCHOR_SUBTITLE_MAX_CHARS ? `${text.slice(0, ANCHOR_SUBTITLE_MAX_CHARS).trim()}…` : text

/**
 * Scroll the editor's view to the Slate element with the given id. The anchor
 * `data-element-id` is set on `core-block.tsx`'s InnerTag at render time. If the
 * element isn't on screen (e.g., not rendered in performance mode for the current
 * song view), this is a no-op — the panel still expands the row.
 */
const scrollEditorToElement = (elementId: string): void => {
  const node = document.querySelector<HTMLElement>(`[data-element-id="${CSS.escape(elementId)}"]`)
  node?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

export interface EntityNotesProps extends SidebarComponent {
  /**
   * Default `noteType` stamped on notes created here. Performance-mode wrappers pass `'performance'`;
   * normal-editor sidebar leaves this undefined so notes default to `'general'`.
   */
  defaultNoteType?: NoteType
  /**
   * Client-side filter applied to the notes list before rendering. Lets a surface narrow
   * by `noteType` (e.g. perf mode shows only `'performance'`) without an extra query.
   */
  filter?: (note: Persisted<Note>) => boolean
}

const contentFilter = matchAny(isPdf, isMusicXML)

/** Format a date for the row header. Today renders as "2:14 PM", earlier dates render as "May 8". */
const formatNoteDate = (iso?: string): string => {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const now = new Date()
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  if (sameDay) {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date)
  }
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)
}

const PREVIEW_MAX_CHARS = 40

/** Truncate to the first non-empty line, trimmed and ellipsised at PREVIEW_MAX_CHARS. */
const firstLinePreview = (raw: string): string | null => {
  const firstNonEmpty = raw
    .split('\n')
    .map(line => line.trim())
    .find(Boolean)
  if (!firstNonEmpty) {
    return null
  }
  return firstNonEmpty.length > PREVIEW_MAX_CHARS
    ? `${firstNonEmpty.slice(0, PREVIEW_MAX_CHARS).trim()}…`
    : firstNonEmpty
}

/** Fall-back: read from the entity's `children` snapshot (stale relative to the YDoc). */
const previewFromChildren = (children: CoreElement[] | null | undefined): string | null => {
  if (!Array.isArray(children) || children.length === 0) {
    return null
  }
  const text = children
    .flatMap(block => (Array.isArray(block.children) ? block.children : []))
    .map(leaf => (typeof (leaf as SlateText).text === 'string' ? (leaf as SlateText).text : ''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
  return firstLinePreview(text)
}

/** Sort notes most-recently-updated first; falls back to createdAt when updatedAt is missing. */
const sortByMostRecent = (a: Persisted<Note>, b: Persisted<Note>) => {
  const aKey = a.updatedAt ?? a.createdAt ?? ''
  const bKey = b.updatedAt ?? b.createdAt ?? ''
  return bKey.localeCompare(aKey)
}

interface NoteRowProps {
  note: Persisted<Note>
  expanded: boolean
  /**
   * When true, focus the note's contenteditable on mount-or-when-this-flips-true.
   * Caller controls the trigger so we don't steal focus on initial panel render
   * (only after a user-initiated create or expand).
   */
  autoFocus: boolean
  /**
   * Truncated source-line snippet for the row's anchor subtitle. Undefined when
   * the note is unanchored or the target element is missing from the snapshot
   * (silent stale-anchor fallback). Computed by the parent so the children-walk
   * runs once per song, not per row.
   */
  anchorPreview: string | undefined
  onExpand: () => void
  NoteEditor: ReturnType<typeof useEditorClass>
}

const NoteRow: FC<NoteRowProps> = ({ note, expanded, autoFocus, anchorPreview, onExpand, NoteEditor }) => {
  const dispatch = useDispatch()
  const targetElementId = note.targetElementId

  // Hover/focus on a row previews the matching block's halo without scrolling.
  // Both pointer (`onMouseEnter`/`Leave`) and keyboard (`onFocus`/`Blur`)
  // surface the same Redux value so screen-reader / Tab users get parity.
  // We clear unconditionally on leave/blur — if the user is mousing onto a
  // sibling row, that row's onMouseEnter immediately overwrites, no flicker.
  const handlePreviewEnter = useCallback(() => {
    if (targetElementId) {
      dispatch(features.ui.actions.setHoveredAnchoredElementId(targetElementId))
    }
  }, [dispatch, targetElementId])
  const handlePreviewLeave = useCallback(() => {
    dispatch(features.ui.actions.setHoveredAnchoredElementId(null))
  }, [dispatch])

  // Stable id for the anchor caption so the row's `aria-describedby` resolves.
  // Note ids are short uuids — no dom-collision risk in practice. Per-row scope.
  const captionId = `note-${note.id}-anchor`
  const trash = graphHooks.useEntityTrash(note)
  const [showConfirm, setShowConfirm] = useState(false)

  // The expanded card wraps the Slate editor. We focus the contenteditable inside
  // it when autoFocus is requested. The editor mounts asynchronously (collab init,
  // lazy editor class), so we poll briefly until the contenteditable appears.
  const cardRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!expanded || !autoFocus) {
      return
    }
    let attempts = 0
    let timer: number | null = null
    const tick = () => {
      const card = cardRef.current
      const ce = card?.querySelector<HTMLElement>('[contenteditable="true"]')
      if (ce) {
        ce.focus()
        return
      }
      attempts += 1
      if (attempts < 20) {
        timer = window.setTimeout(tick, 50)
      }
    }
    timer = window.setTimeout(tick, 0)
    return () => {
      if (timer !== null) {
        window.clearTimeout(timer)
      }
    }
  }, [autoFocus, expanded])

  const date = formatNoteDate(note.updatedAt ?? note.createdAt)

  // Live YDoc content lives in S3 (the entity's inline `yDoc` field is the seed
  // and isn't updated as the user types). Fetch the S3 bytes when storageUrls are
  // valid; RTK Query caches across components so this is one request per entity
  // regardless of how many rows want a preview.
  const validUrls =
    note.storageUrls?.expiresAtISODateTime !== undefined &&
    note.storageUrls.expiresAtISODateTime > new Date().toISOString()
  const { currentData: yDocBytes } = storageApi.useGetAuthorizedStorageFileQuery(
    validUrls && note.storageUrls ? { storageUrls: note.storageUrls } : skipToken
  )

  const preview = useMemo(() => {
    // 1. S3 bytes — canonical for live content.
    const fromS3 = firstLinePreview(textFromYDoc(yDocBytes))
    if (fromS3) {
      return fromS3
    }
    // 2. Inline yDoc field — legacy or fresh-create seed.
    const fromInline = firstLinePreview(textFromYDoc(note.yDoc))
    if (fromInline) {
      return fromInline
    }
    // 3. Last resort — the children snapshot.
    return previewFromChildren(note.children as CoreElement[] | null | undefined)
  }, [yDocBytes, note.yDoc, note.children])

  // Heuristic: when none of the three text sources have content, the note may
  // still have live content in the editor's YDoc that hasn't reached us yet
  // (typical for a freshly-created note before its first S3 sync). In that case
  // `updatedAt` is meaningfully after `createdAt` because the YDoc has been
  // edited. Suppress the "Tap to write" placeholder in this window — it lies
  // about the note being empty and the user sees the real text after refresh.
  const likelyEdited = useMemo(() => {
    const created = note.createdAt ? new Date(note.createdAt).getTime() : 0
    const updated = note.updatedAt ? new Date(note.updatedAt).getTime() : 0
    return Number.isFinite(created) && Number.isFinite(updated) && updated - created > 2_000
  }, [note.createdAt, note.updatedAt])

  const handleConfirmDelete = () => {
    setShowConfirm(false)
    trash()
  }

  if (!expanded) {
    // Three-way fallback for the row's secondary text:
    //  - any text source (S3, inline, children) → show preview
    //  - all empty but `updatedAt > createdAt` → render nothing (avoids the
    //    misleading "Tap to write" for a fresh note whose content hasn't
    //    reached the panel yet)
    //  - all empty and apparently untouched → invite with "Tap to write"
    return (
      <button
        type="button"
        className={styles.row}
        onClick={onExpand}
        onMouseEnter={handlePreviewEnter}
        onMouseLeave={handlePreviewLeave}
        onFocus={handlePreviewEnter}
        onBlur={handlePreviewLeave}
        aria-expanded={false}
        aria-describedby={anchorPreview ? captionId : undefined}
      >
        <div className={styles.rowMain}>
          <span className={styles.rowDate}>{date}</span>
          {preview ? (
            <span className={styles.rowPreview}>{preview}</span>
          ) : likelyEdited ? null : (
            <span className={styles.rowPreviewEmpty}>Tap to write</span>
          )}
        </div>
        {anchorPreview ? (
          <div id={captionId} className={styles.rowAnchor}>
            <span aria-hidden>→ </span>
            <span>“{anchorPreview}”</span>
            <span className={styles.srOnly}>. Press Enter to jump.</span>
          </div>
        ) : null}
      </button>
    )
  }

  return (
    <div
      ref={cardRef}
      className={styles.expandedCard}
      onMouseEnter={handlePreviewEnter}
      onMouseLeave={handlePreviewLeave}
    >
      <div className={styles.expandedHeader}>
        <span className={styles.expandedDate}>{date}</span>
        <Tooltip title="Delete note">
          <IconButton aria-label="Delete note" onClick={() => setShowConfirm(true)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      {anchorPreview ? (
        <div className={styles.expandedAnchor}>
          <span aria-hidden>→ </span>
          <span>“{anchorPreview}”</span>
        </div>
      ) : null}
      <div className={styles.expandedEditor}>
        {NoteEditor ? <NoteEditor element={note} gutter={false} /> : null}
      </div>
      <ConfirmDialog
        open={showConfirm}
        title="Delete this note?"
        text="This can't be undone. The note and its history will be removed."
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

export const EntityNotes: FC<EntityNotesProps> = ({ entity, style: styleProp, defaultNoteType, filter }) => {
  const { entities: notes, isSuccess } = graphHooks.useEntityChildren<Note>({ parentId: entity?.id, filter: 'note' })
  const { userId } = useCurrentUser()

  const { createEntity } = graphHooks.useEntityCreate<Note>()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // The note id whose editor should auto-focus on next mount. Distinct from
  // expandedId — initial default-expand on panel open should NOT steal focus
  // from the song surface, only user-initiated expand or create should.
  const [autoFocusId, setAutoFocusId] = useState<string | null>(null)

  const visibleNotes = filter ? notes.filter(filter) : notes
  const sortedNotes = [...visibleNotes].sort(sortByMostRecent)
  const hasNotes = isSuccess ? sortedNotes.length > 0 : undefined

  // On first load, expand the most-recently-updated note. Effect is keyed on the
  // *count* of notes — once a note is expanded we never reset the choice based on
  // re-fetches. New notes the user creates set expandedId explicitly via the
  // create handler (below).
  useEffect(() => {
    if (expandedId !== null) {
      return
    }
    if (sortedNotes.length === 0) {
      return
    }
    setExpandedId(sortedNotes[0].id)
  }, [expandedId, sortedNotes])

  const handleCreateNote = useCallback(async () => {
    const id = shortUuid()
    const { yDoc } = createCollaborativeDoc({ id, nodes: [{ type: 'line', children: [{ text: '' }] }] })
    const draft = { ...noteTemplate({ id, parentId: entity.id, userId, noteType: defaultNoteType }), yDoc } as Note
    // Not optimistic: the note opens at once in a collab editor, which needs it to exist (#1898)
    const persisted = await createEntity({ entity: draft, parent: entity, isPrivate: true, optimistic: false })
    const newId = persisted?.id ?? null
    setExpandedId(newId)
    setAutoFocusId(newId)
  }, [createEntity, defaultNoteType, entity, userId])

  // Build a quick id → note lookup so the expand handler can reach the note's
  // `targetElementId` to scroll the editor without re-iterating the list.
  const noteById = useMemo(() => new Map(visibleNotes.map(n => [n.id, n])), [visibleNotes])

  const handleExpand = useCallback(
    (id: string) => () => {
      setExpandedId(id)
      setAutoFocusId(id)
      const targetId = noteById.get(id)?.targetElementId
      if (targetId) {
        scrollEditorToElement(targetId)
      }
    },
    [noteById]
  )

  // Consume `pendingAnchoredNoteId` from Redux. Set by the personal-note plugin's
  // controller when an anchor marker fires `'show-anchored-note'` or a new note
  // is created from the block menu / slash command. Here we use it to expand the
  // matching row and clear the pending state. Scroll-to-element is intentionally
  // skipped on this path — the user JUST clicked the marker, they don't need the
  // editor to scroll back to the same place they were.
  const dispatch = useDispatch()
  const pendingAnchoredNoteId = useSelector((state: RootState) => state.ui.pendingAnchoredNoteId)
  useEffect(() => {
    if (!pendingAnchoredNoteId) {
      return
    }
    if (noteById.has(pendingAnchoredNoteId)) {
      setExpandedId(pendingAnchoredNoteId)
      setAutoFocusId(pendingAnchoredNoteId)
      dispatch(features.ui.actions.setPendingAnchoredNoteId(null))
    }
  }, [dispatch, noteById, pendingAnchoredNoteId])

  // Mirror the currently-expanded note's anchor into Redux so the editor side
  // can render the strong `data-anchored-active` highlight + 600ms spotlight on
  // the matching block. Cleared when no row is expanded, when the expanded note
  // is unanchored (target deleted falls into this branch — `targetElementId`
  // resolves but `noteById.get` may miss; the resulting `null` matches no
  // block, so the editor stays in baseline state). Cleanup on unmount keeps
  // entity-switching from leaving a stale active id behind.
  useEffect(() => {
    const expandedNote = expandedId ? noteById.get(expandedId) : undefined
    const elementId = expandedNote?.targetElementId ?? null
    dispatch(features.ui.actions.setActiveAnchoredElementId(elementId))
    return () => {
      dispatch(features.ui.actions.setActiveAnchoredElementId(null))
    }
  }, [dispatch, expandedId, noteById])

  // Per-row anchor preview text — computed once per song-children change.
  // The Slate children of the entity aren't on the `Persisted<Entity>` shape
  // directly (it's a CoreElement extension); cast via `unknown` to read.
  const songChildren = (entity as { children?: unknown } | undefined)?.children
  const elementTextById = useMemo(() => buildElementTextById(songChildren), [songChildren])

  const { entities, isLoading } = graphHooks.useEntityChildren({
    parentId: entity?.id,
    filter: contentFilter,
  })
  const [deleteEntity, { isLoading: isDeleting }] = graphHooks.useEntityDelete()

  const NoteEditor = useEditorClass('NoteEditor')
  const { mimeTypeAccept } = useMimeTypes()

  const plugins = usePlugins('all') ?? []

  const headerLabel = sortedNotes.length > 0 ? `Notes · ${sortedNotes.length}` : 'Notes'

  return (
    <VBox style={{ flex: 1, minHeight: 0, ...styleProp }}>
      <section className={styles.section} aria-label="Personal notes">
        <div className={styles.headerRow}>
          <span className={styles.headerTitle}>{headerLabel}</span>
          <Button
            className={styles.newButton}
            size="small"
            variant="text"
            startIcon={<Add fontSize="small" />}
            onClick={handleCreateNote}
          >
            New
          </Button>
        </div>

        <div className={styles.sectionScroll}>
          {hasNotes === false ? (
            <div className={styles.emptyStateBox}>
              <Typography className={styles.emptyStateText} variant="body2">
                No notes yet. Jot down anything you want to remember — capo position, tricky transitions, lyric cues.
                Only you can see these.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={handleCreateNote}
              >
                New note
              </Button>
            </div>
          ) : null}

          {sortedNotes.map(note => {
            const text = note.targetElementId ? elementTextById.get(note.targetElementId) : undefined
            return (
              <NoteRow
                key={note.id}
                note={note}
                expanded={expandedId === note.id}
                autoFocus={autoFocusId === note.id}
                anchorPreview={text ? truncateAnchorPreview(text) : undefined}
                onExpand={handleExpand(note.id)}
                NoteEditor={NoteEditor}
              />
            )
          })}
        </div>
      </section>

      <section className={styles.section} aria-label="Documents">
        <div className={styles.headerRow}>
          <span className={styles.headerTitle}>Documents</span>
        </div>

        <div className={styles.sectionScroll}>
          {isLoading ? <LinearProgress /> : null}
          {entities.map(doc => (
            <Fragment key={doc.id}>
              <EntityContent key={doc.id} entity={doc} plugins={plugins} />
              <Button disabled={isDeleting} color="error" onClick={() => deleteEntity({ entity: doc })}>
                Delete
              </Button>
            </Fragment>
          ))}
          <UploadStoredEntity
            parentId={entity.id}
            contentHint="MusicXML or PDF"
            isPrivate={true}
            accept={mimeTypeAccept}
          />
        </div>
      </section>
    </VBox>
  )
}
